import React from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import { ORG_LEGAL_NAME, ORG_MAILING_ADDRESS } from '../config';

interface Props { navigate: (s: Screen) => void; user: User | null; }

const h2: React.CSSProperties = { fontSize: 18, marginTop: 28, marginBottom: 8 };
const p: React.CSSProperties = { fontSize: 15, lineHeight: 1.7, color: 'var(--charcoal)', marginBottom: 12 };

export default function Terms({ navigate, user }: Props) {
  const home: Screen = user ? 'dashboard' : 'landing';
  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate(home)} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Terms of Service</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 28 }}>
        <h1 style={{ fontSize: 26, marginBottom: 6 }}>Terms of Service</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>Effective date: August 4, 2026</p>

        <p style={p}>
          These Terms of Service ("Terms") are a legal agreement between you and the
          Brain Health and Wellness Coach Institute ("BHWCI," "we," "us," or "our"),
          which operates the Decode My Student application and website (the "Service").
          By creating an account or using the Service, you agree to these Terms. If you
          do not agree, do not use the Service.
        </p>

        <h2 style={h2}>1. What Decode My Student is</h2>
        <p style={p}>
          Decode My Student is a professional-support tool for K-12 educators. You
          describe a classroom situation in your own words, and the Service returns
          plain-language explanations grounded in brain science along with practical
          strategies to try. It also lets you keep lightweight notes and track which
          strategies are working.
        </p>

        <h2 style={h2}>2. Not medical, clinical, or diagnostic advice</h2>
        <p style={p}>
          The Service is decision-support for educators — it is <strong>not</strong> a
          medical, psychological, psychiatric, or clinical service, and it does not
          diagnose, treat, or provide a professional opinion about any child. The
          responses are generated by artificial intelligence and may be incomplete or
          wrong. Use your own professional judgment. Nothing in the Service replaces
          evaluation or care by a qualified professional, and it should never be used
          in a medical or safety emergency. If a child may be in danger, contact your
          school's crisis procedures or emergency services.
        </p>

        <h2 style={h2}>3. Eligibility and your account</h2>
        <p style={p}>
          You must be at least 18 years old and a current or prospective educator to
          use the Service. You are responsible for keeping your password secure and for
          everything done through your account. Tell us right away if you suspect
          unauthorized use.
        </p>

        <h2 style={h2}>4. How you must use the Service</h2>
        <p style={p}>
          To protect the students you write about, you agree to identify students by
          initials and grade level only. Do not enter full names, dates of birth,
          student ID numbers, home addresses, medical record numbers, or other directly
          identifying details. You agree not to misuse the Service, attempt to break its
          security, or use it in a way that violates your school's or district's policies
          or any applicable law.
        </p>

        <h2 style={h2}>5. Free trial, subscriptions, and billing</h2>
        <p style={p}>
          New accounts include a free 7-day trial with no credit card required. After the
          trial, continued access requires a paid subscription: <strong>$29 per month</strong>
          {' '}or <strong>$299 per year</strong>, unless you are covered by a school or
          district license. Paid plans are billed in advance through our payment processor,
          Stripe, and renew automatically at the end of each billing period until you cancel.
        </p>
        <p style={p}>
          You can cancel at any time from within the app; your access continues through the
          end of the period you have already paid for, and it does not renew after that.
          Except where required by law, payments are non-refundable and we do not provide
          partial-period refunds. Prices may change on a going-forward basis, and we will
          give you notice before a change affects you.
        </p>

        <h2 style={h2}>6. Your content</h2>
        <p style={p}>
          The situations, notes, and ratings you enter are yours. You grant us a limited
          license to process them solely to operate and provide the Service to you —
          including sending the text you submit to our AI provider to generate a response,
          as described in our Privacy Policy. We do not sell your content, and we do not
          use it to advertise to you.
        </p>

        <h2 style={h2}>7. Deleting your account</h2>
        <p style={p}>
          You can permanently delete your account and all associated data at any time from
          Account settings. Deletion is immediate and cannot be undone. See our Privacy
          Policy for exactly what is stored and what is removed.
        </p>

        <h2 style={h2}>8. Service availability and changes</h2>
        <p style={p}>
          We work to keep the Service running but do not guarantee it will be
          uninterrupted or error-free. We may add, change, or remove features, and we may
          suspend or end the Service, with reasonable notice where practical.
        </p>

        <h2 style={h2}>9. Disclaimers</h2>
        <p style={p}>
          The Service is provided "as is" and "as available," without warranties of any
          kind, whether express or implied, including merchantability, fitness for a
          particular purpose, and non-infringement. We do not warrant that any strategy or
          explanation the Service provides will be accurate, effective, or suitable for a
          particular student or situation.
        </p>

        <h2 style={h2}>10. Limitation of liability</h2>
        <p style={p}>
          To the fullest extent permitted by law, BHWCI will not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or for any
          loss of data, arising out of or relating to your use of the Service. Our total
          liability for any claim relating to the Service will not exceed the amount you
          paid us for the Service in the 12 months before the claim.
        </p>

        <h2 style={h2}>11. Governing law</h2>
        <p style={p}>
          These Terms are governed by the laws of the State of Delaware, without regard to
          its conflict-of-laws rules. Any dispute will be subject to the exclusive
          jurisdiction of the state and federal courts located in Delaware.
        </p>

        <h2 style={h2}>12. Changes to these Terms</h2>
        <p style={p}>
          We may update these Terms from time to time. If we make a material change, we
          will update the effective date above and, where appropriate, notify you. Your
          continued use of the Service after a change means you accept the updated Terms.
        </p>

        <h2 style={h2}>13. Contact</h2>
        <p style={p}>
          Questions about these Terms? Use the <span style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('contact')}>Contact Us</span> form{ORG_MAILING_ADDRESS ? ', or write to us at:' : '.'}
        </p>
        {ORG_MAILING_ADDRESS && (
          <p style={p}>{ORG_LEGAL_NAME}<br />{ORG_MAILING_ADDRESS}</p>
        )}
      </div>
    </div>
  );
}
